import { Action, Reducer } from 'redux';

// Actions
import * as Actions from '../actions';

// state
import {
    CashFlowStatisticsState,
    CashFlowStatisticsUnloadState
} from '../states/CashFlow.Statistics.State';

type KnownAction =
    | Actions.CashFlowStatistics.CashFlowStatisticsGetOverviewRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetOverviewSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetOverviewFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelayRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelaySuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelayFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetMonthlyRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetMonthlySuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetMonthlyFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopDebtorsRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopDebtorsSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopDebtorsFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopCreditorsRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopCreditorsSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetTopCreditorsFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetAgingRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetAgingSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetAgingFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetCashflowSeriesRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetCashflowSeriesSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetCashflowSeriesFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetDebtSeriesRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetDebtSeriesSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetDebtSeriesFailureAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelaySeriesRequestAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelaySeriesSuccessAction
    | Actions.CashFlowStatistics.CashFlowStatisticsGetPaymentDelaySeriesFailureAction;

const loading = { isLoading: true, success: undefined as boolean | undefined, failed: false };
const ok = { isLoading: false, success: true as boolean | undefined, failed: false };
const fail = { isLoading: false, success: false as boolean | undefined, failed: true };

export const CashFlowStatisticsReducer: Reducer<CashFlowStatisticsState> = (
    state: CashFlowStatisticsState | undefined,
    incomingAction: Action
): CashFlowStatisticsState => {
    if (state === undefined) {
        return CashFlowStatisticsUnloadState;
    }

    const action = incomingAction as KnownAction;

    switch (action.type) {
        // ---- Overview ----
        case 'CASHFLOW-STATISTICS_GETOVERVIEW_REQUEST':
            return { ...state, overviewStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETOVERVIEW_SUCCESS':
            return { ...state, overview: action.payload, overviewStatus: { ...ok } };
        case 'CASHFLOW-STATISTICS_GETOVERVIEW_FAILURE':
            return { ...state, overviewStatus: { ...fail }, error: action.error };

        // ---- Payment delay ----
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAY_REQUEST':
            return { ...state, paymentDelayStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAY_SUCCESS':
            return {
                ...state,
                paymentDelay: action.payload,
                paymentDelayStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAY_FAILURE':
            return { ...state, paymentDelayStatus: { ...fail }, error: action.error };

        // ---- Monthly cashflow ----
        case 'CASHFLOW-STATISTICS_GETMONTHLY_REQUEST':
            return { ...state, monthlyCashflowStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETMONTHLY_SUCCESS':
            return {
                ...state,
                monthlyCashflow: action.payload,
                monthlyCashflowStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETMONTHLY_FAILURE':
            return { ...state, monthlyCashflowStatus: { ...fail }, error: action.error };

        // ---- Top debtors ----
        case 'CASHFLOW-STATISTICS_GETTOPDEBTORS_REQUEST':
            return { ...state, topDebtorsStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETTOPDEBTORS_SUCCESS':
            return {
                ...state,
                topDebtors: action.payload,
                topDebtorsStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETTOPDEBTORS_FAILURE':
            return { ...state, topDebtorsStatus: { ...fail }, error: action.error };

        // ---- Top creditors ----
        case 'CASHFLOW-STATISTICS_GETTOPCREDITORS_REQUEST':
            return { ...state, topCreditorsStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETTOPCREDITORS_SUCCESS':
            return {
                ...state,
                topCreditors: action.payload,
                topCreditorsStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETTOPCREDITORS_FAILURE':
            return { ...state, topCreditorsStatus: { ...fail }, error: action.error };

        // ---- Aging ----
        case 'CASHFLOW-STATISTICS_GETAGING_REQUEST':
            return { ...state, agingStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETAGING_SUCCESS':
            return { ...state, aging: action.payload, agingStatus: { ...ok } };
        case 'CASHFLOW-STATISTICS_GETAGING_FAILURE':
            return { ...state, agingStatus: { ...fail }, error: action.error };

        // ---- Cashflow series ----
        case 'CASHFLOW-STATISTICS_GETCASHFLOWSERIES_REQUEST':
            return { ...state, cashflowSeriesStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETCASHFLOWSERIES_SUCCESS':
            return {
                ...state,
                cashflowSeries: action.payload,
                cashflowSeriesStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETCASHFLOWSERIES_FAILURE':
            return { ...state, cashflowSeriesStatus: { ...fail }, error: action.error };

        // ---- Debt series ----
        case 'CASHFLOW-STATISTICS_GETDEBTSERIES_REQUEST':
            return { ...state, debtSeriesStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETDEBTSERIES_SUCCESS':
            return {
                ...state,
                debtSeries: action.payload,
                debtSeriesStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETDEBTSERIES_FAILURE':
            return { ...state, debtSeriesStatus: { ...fail }, error: action.error };

        // ---- Payment delay series ----
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAYSERIES_REQUEST':
            return { ...state, paymentDelaySeriesStatus: { ...loading }, error: undefined };
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAYSERIES_SUCCESS':
            return {
                ...state,
                paymentDelaySeries: action.payload,
                paymentDelaySeriesStatus: { ...ok }
            };
        case 'CASHFLOW-STATISTICS_GETPAYMENTDELAYSERIES_FAILURE':
            return { ...state, paymentDelaySeriesStatus: { ...fail }, error: action.error };

        default:
            return state;
    }
};
